import { ChevronRight, Globe, KeyRound, Loader2, MonitorSmartphone, Package, Save, ShieldAlert, ShieldCheck, UserRound, type LucideIcon } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { ApiError } from "@hostdock/sdk";
import type { HostingUsage } from "@hostdock/types";
import { cn, ErrorState, Field, formatBytes, FormError, Input, Skeleton, toast } from "@hostdock/ui";
import { api, SESSION_KEY } from "../api";
import { DetailCard, PageHero, Pill, primaryButtonClass, StatCard } from "../components/page-kit";
import { useSession } from "../session";

export function AccountPage() {
  const qc = useQueryClient();
  const { session } = useSession();
  const q = useQuery({ queryKey: ["profile"], queryFn: api.profile });
  const usage = useQuery({ queryKey: ["hosting-usage"], queryFn: api.usage });
  const home = useQuery({ queryKey: ["home"], queryFn: api.home });
  const [form, setForm] = useState({ name: "", displayName: "", companyName: "", phone: "" });
  useEffect(() => {
    if (q.data) setForm({ name: q.data.name, displayName: q.data.customer.displayName, companyName: q.data.customer.companyName ?? "", phone: q.data.customer.phone ?? "" });
  }, [q.data]);
  const save = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.updateProfile({ name: form.name, displayName: form.displayName, companyName: form.companyName || null, phone: form.phone || null }),
    onSuccess: (p) => {
      qc.setQueryData(["profile"], p);
      void qc.invalidateQueries({ queryKey: SESSION_KEY });
      toast.success("Profile saved");
    },
  });
  if (q.error) return <div className="hd-card p-6 lg:mx-[13px]"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>;
  const fe = save.error instanceof ApiError ? save.error.fieldErrors : {};
  const upd = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const dirty = !!q.data && (form.name !== q.data.name || form.displayName !== q.data.customer.displayName || form.companyName !== (q.data.customer.companyName ?? "") || form.phone !== (q.data.customer.phone ?? ""));

  const subs = usage.data?.subscriptions ?? [];
  const plans = subs.map((s) => s.plan.name);
  const siteLimit = subs.reduce((a, s) => a + s.limits.websites, 0);
  const status = session.customer.status;
  const mfa = session.user.mfaEnabled;
  const initials = (q.data?.name ?? session.user.name ?? "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");

  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero icon={UserRound} title="Account" description="Your contact details, your hosting plan and how you sign in." />

      <div className="mt-[10px] grid grid-cols-2 gap-[11px] xl:grid-cols-4">
        <StatCard icon={Package} tile="teal" label="Plan" value={plans.length ? plans.join(" · ") : usage.isPending ? "…" : "No plan"} sub={subs.length > 1 ? `${subs.length} hosting packages` : subs.length ? "Your hosting package" : usage.isPending ? undefined : "Contact support to get one"} />
        <StatCard icon={ShieldCheck} tile={status === "ACTIVE" ? "green" : "orange"} label="Status" value={status.charAt(0) + status.slice(1).toLowerCase()} sub={status === "ACTIVE" ? "Everything is running" : "Contact support"} />
        <StatCard icon={Globe} tile="blue" label="Websites" value={usage.data ? (siteLimit ? `${usage.data.websites.length} / ${siteLimit}` : usage.data.websites.length) : "…"} sub={siteLimit ? "Used of your plan" : undefined} />
        <StatCard icon={KeyRound} tile={mfa ? "green" : "orange"} label="Two-factor" value={mfa ? "Enabled" : "Off"} sub={mfa ? "Codes from your app" : "Recommended"} />
      </div>

      <div className="mt-[15px] grid items-start gap-[15px] xl:grid-cols-[minmax(0,1fr)_380px]">
        <DetailCard title="Profile" description="Shown on your account and used to contact you.">
          {!q.data ? (
            <Skeleton className="h-40 w-full rounded-xl" />
          ) : (
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate();
              }}
              noValidate
            >
              <div className="flex items-center gap-[14px] rounded-xl bg-surface-2/70 px-[14px] py-[12px]">
                <span className="flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-[linear-gradient(140deg,#25c6d1,#0a9fb1)] text-[16px] font-semibold text-white" aria-hidden>{initials || <UserRound size={20} />}</span>
                <div className="min-w-0 leading-tight">
                  <p className="truncate text-[14.5px] font-bold">{q.data.name}</p>
                  <p className="mt-[3px] truncate text-[12.5px] text-muted">{q.data.email} · {q.data.customer.displayName}</p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Your name" error={fe.name}>{(p) => <Input {...p} value={form.name} onChange={upd("name")} autoComplete="name" />}</Field>
                <Field label="Email" hint="Contact support to change your sign-in email.">{(p) => <Input {...p} value={q.data.email} disabled />}</Field>
                <Field label="Account name" error={fe.displayName}>{(p) => <Input {...p} value={form.displayName} onChange={upd("displayName")} />}</Field>
                <Field label="Company" error={fe.companyName}>{(p) => <Input {...p} value={form.companyName} onChange={upd("companyName")} autoComplete="organization" />}</Field>
                <Field label="Phone" error={fe.phone}>{(p) => <Input {...p} value={form.phone} onChange={upd("phone")} autoComplete="tel" />}</Field>
              </div>
              {save.error && !Object.keys(fe).length ? <FormError error={save.error} /> : null}
              <div className="flex flex-wrap items-center gap-3 border-t border-line pt-4">
                <button type="submit" disabled={save.isPending || !dirty} className={primaryButtonClass}>
                  {save.isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save profile
                </button>
                {!dirty && <span className="text-[12px] text-subtle">No unsaved changes</span>}
              </div>
            </form>
          )}
        </DetailCard>

        <div className="flex flex-col gap-[15px]">
          <DetailCard title="Security & sign-in" description="Manage these from the Security page." actions={<Link to="/security" className="text-[12.5px] font-semibold text-accent hover:underline">Open</Link>}>
            <ul className="-my-1 flex flex-col divide-y divide-line">
              <AccountLink
                icon={mfa ? ShieldCheck : ShieldAlert}
                tile={mfa ? "bg-[#e2f8ee] text-[#14a04a]" : "bg-[#fdeedf] text-[#e0611f]"}
                label="Two-factor authentication"
                detail={mfa ? "Enabled on your account" : "Not enabled yet"}
                trailing={<Pill tone={mfa ? "ok" : "wait"} dot>{mfa ? "Enabled" : "Off"}</Pill>}
              />
              <AccountLink icon={KeyRound} tile="bg-[#d6f5f7] text-[#0a8ea0]" label="Password" detail="Change your sign-in password" />
              <AccountLink
                icon={MonitorSmartphone}
                tile="bg-[#e6f0fc] text-[#2f6fd6]"
                label="Signed-in sessions"
                detail="Review where you're signed in"
                trailing={<span className="text-[13px] font-semibold tabular-nums">{home.data ? home.data.security.activeSessions : "—"}</span>}
              />
            </ul>
          </DetailCard>
          <PlanCard usage={usage.data} loading={usage.isPending} />
        </div>
      </div>
    </div>
  );
}

function AccountLink({ icon: Icon, tile, label, detail, trailing }: { icon: LucideIcon; tile: string; label: string; detail: string; trailing?: ReactNode }) {
  return (
    <li>
      <Link to="/security" className="group -mx-2 flex items-center gap-3 rounded-xl px-2 py-[10px] transition-colors hover:bg-surface-2">
        <span className={cn("flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-xl dark:bg-surface-3", tile)}><Icon size={18} strokeWidth={1.8} /></span>
        <span className="min-w-0 flex-1 leading-tight">
          <span className="block text-[13.5px] font-semibold">{label}</span>
          <span className="mt-[3px] block truncate text-[12px] text-muted">{detail}</span>
        </span>
        {trailing}
        <ChevronRight size={16} className="shrink-0 text-subtle transition-transform group-hover:translate-x-0.5" />
      </Link>
    </li>
  );
}

/** What each hosting package allows and how much of it is used, as measured by HostDock. */
function PlanCard({ usage, loading }: { usage: HostingUsage | undefined; loading: boolean }) {
  const subs = usage?.subscriptions ?? [];
  return (
    <DetailCard title="Your plan" description="What your hosting package includes and how much is in use.">
      {loading ? <Skeleton className="h-28 w-full rounded-xl" /> : subs.length === 0 ? (
        <p className="text-[12.5px] text-muted">You don't have a hosting package yet. Contact support to get one.</p>
      ) : (
        <div className="flex flex-col gap-5">
          {subs.map((s) => (
            <div key={s.id}>
              {subs.length > 1 && <p className="mb-2 text-[13px] font-semibold">{s.plan.name}</p>}
              <ul className="flex flex-col gap-[12px]">
                <Meter label="Websites" used={s.usage.websites} limit={s.limits.websites} text={`${s.usage.websites} of ${s.limits.websites}`} />
                <Meter label="Storage" used={s.usage.storageBytes} limit={s.limits.storageGb * 1024 ** 3} text={s.usage.storageBytes == null ? `Not measured yet · ${s.limits.storageGb} GB` : `${formatBytes(s.usage.storageBytes)} of ${s.limits.storageGb} GB`} />
                <Meter label="Databases" used={s.usage.databases} limit={s.limits.databases} text={`${s.usage.databases} of ${s.limits.databases}`} />
                <Meter label="Cron jobs" used={s.usage.cronJobs} limit={s.limits.cronJobs} text={`${s.usage.cronJobs} of ${s.limits.cronJobs}`} />
              </ul>
              <p className="mt-[12px] text-[11.5px] leading-[17px] text-subtle">
                {s.limits.cpuCores} CPU core{s.limits.cpuCores === 1 ? "" : "s"} · {s.limits.memoryMb >= 1024 ? `${+(s.limits.memoryMb / 1024).toFixed(1)} GB` : `${s.limits.memoryMb} MB`} memory · backups kept {s.limits.backupRetentionDays} days
                {s.limits.mailboxes ? ` · ${s.limits.mailboxes} mailboxes` : ""}
              </p>
            </div>
          ))}
        </div>
      )}
    </DetailCard>
  );
}

function Meter({ label, used, limit, text }: { label: string; used: number | null; limit: number; text: string }) {
  const pct = used == null || !limit ? 0 : Math.min(100, Math.round((used / limit) * 100));
  return (
    <li>
      <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
        <span className="font-medium">{label}</span>
        <span className="truncate text-muted tabular-nums">{text}</span>
      </div>
      <div className="mt-[6px] h-[7px] overflow-hidden rounded-full bg-surface-3" role="progressbar" aria-label={label} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className={cn("h-full rounded-full", pct >= 90 ? "bg-danger" : pct >= 75 ? "bg-[#e0611f]" : "bg-[#048d9f]")} style={{ width: `${pct}%` }} />
      </div>
    </li>
  );
}
